// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize, hasPermission } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const MetricsView = ({ user = null, onDrillThrough = undefined }: { user?: UserRow | null; onDrillThrough?: any }) => {
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [trend, setTrend] = useState<any>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState<any>(null);
  const [comparisons, setComparisons] = useState<Record<string, any>>({});
  const [comparePeriod, setComparePeriod] = useState(7);
  const [alertThreshold, setAlertThreshold] = useState("");
  const [alertDirection, setAlertDirection] = useState("above");
  const [alertRecipients, setAlertRecipients] = useState("");
  const [alertMsg, setAlertMsg] = useState<any>(null);

  const { selected: clientScopeSelected } = useClientScope();
  useEffect(() => {
    apiCall("/reports/metrics/current").then(d => { setMetrics(d.metrics || []); setLoading(false); }).catch(() => setLoading(false));
    apiCall("/reports/metrics/compare?days=7").then(d => {
      const map: Record<string, any> = {};
      (d.comparisons || []).forEach((c: any) => { map[c.metric_key] = c; });
      setComparisons(map);
    }).catch(() => {});
  }, [clientScopeSelected]);

  const loadTrend = async (key: string) => {
    setSelectedMetric(key);
    setTrendLoading(true);
    const m = metrics.find((x: any) => x.metric_key === key);
    setAlertThreshold(m?.alert_threshold != null ? String(m.alert_threshold) : "");
    setAlertDirection(m?.alert_direction || "above");
    setAlertRecipients((m?.alert_recipients || []).join(", "));
    setAlertMsg(null);
    try {
      const d = await apiCall(`/reports/metrics/${key}/trend?days=30`);
      setTrend(d);
    } catch { setTrend(null); }
    setTrendLoading(false);
  };

  const saveAlert = async () => {
    if (!selectedMetric) return;
    setAlertMsg("Saving...");
    try {
      const recipients = alertRecipients.split(",").map(e => e.trim()).filter(Boolean);
      await apiCall(`/reports/metrics/${selectedMetric}/alert`, { method: "PUT", body: JSON.stringify({ threshold: alertThreshold ? Number(alertThreshold) : null, direction: alertDirection, recipients }) });
      setAlertMsg("Saved");
      const updated = metrics.map((m: any) => m.metric_key === selectedMetric ? { ...m, alert_threshold: alertThreshold ? Number(alertThreshold) : null, alert_direction: alertDirection, alert_recipients: recipients } : m);
      setMetrics(updated);
    } catch { setAlertMsg("Failed"); }
    setTimeout(() => setAlertMsg(null), 3000);
  };

  const captureSnapshot = async () => {
    setSnapshotMsg("Capturing...");
    try {
      const d = await apiCall("/reports/metrics/snapshot", { method: "POST" });
      setSnapshotMsg(`Captured ${d.success}/${d.total} metrics for ${d.date}`);
      apiCall("/reports/metrics/current").then(d => setMetrics(d.metrics || [])).catch(() => {});
    } catch { setSnapshotMsg("Failed"); }
    setTimeout(() => setSnapshotMsg(null), 4000);
  };

  const loadComparison = async (days: number) => {
    setComparePeriod(days);
    try {
      const d = await apiCall(`/reports/metrics/compare?days=${days}`);
      const map: Record<string, any> = {};
      (d.comparisons || []).forEach((c: any) => { map[c.metric_key] = c; });
      setComparisons(map);
    } catch {}
  };

  const grouped = metrics.reduce((acc: any, m: any) => { (acc[m.category || ''] = acc[m.category || ''] || []).push(m); return acc; }, {});

  const isWarning = (m: any) => {
    if (m.currentValue == null) return false;
    if (m.metric_key.includes("overdue") && m.currentValue > 0) return true;
    if (m.metric_key.includes("expired") && m.currentValue > 0) return true;
    if (m.metric_key.includes("unassigned") && m.currentValue > 0) return true;
    if (m.metric_key === "metadata_missing_pct" && m.currentValue > 20) return true;
    return false;
  };

  const fmtValue = (m: any) => {
    if (m.currentValue == null) return "—";
    if (m.format === "percent") return `${m.currentValue}%`;
    if (m.format === "days") return `${m.currentValue}d`;
    return Number(m.currentValue).toLocaleString();
  };

  const maxTrend = trend?.trend?.length > 0 ? Math.max(...trend.trend.map((t: any) => Number(t.value) || 0), 1) : 1;

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: T.txM }}>Loading metrics...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 13, color: T.txM }}>{metrics.length} metrics across {Object.keys(grouped).length} categories</div>
          <select value={comparePeriod} onChange={e => loadComparison(Number(e.target.value))} style={{ padding: "4px 8px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, fontFamily: "inherit", color: T.txM }}>
            <option value={7}>vs 7d ago</option>
            <option value={14}>vs 14d ago</option>
            <option value={30}>vs 30d ago</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {snapshotMsg && <span style={{ fontSize: 12, color: T.acc }}>{snapshotMsg}</span>}
          {(hasPermission(user, 'admin.manage_metrics') || user?.role === "admin") && <button onClick={() => { captureSnapshot(); }} style={{ padding: "6px 14px", backgroundColor: T.acc, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Capture Snapshot</button>}
        </div>
      </div>

      {Object.entries(grouped).map(([cat, mets]: [string, any]) => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.txM, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{cat}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
            {mets.map((m: any) => {
              const cmp = comparisons[m.metric_key];
              const deltaColor = cmp?.delta > 0 ? (isWarning(m) ? T.red : T.grn) : cmp?.delta < 0 ? (isWarning(m) ? T.grn : T.red) : T.txM;
              return (
              <div key={m.metric_key} onClick={() => loadTrend(m.metric_key)} style={{ padding: 14, borderRadius: 8, border: `1px solid ${selectedMetric === m.metric_key ? T.acc : T.brd}`, backgroundColor: selectedMetric === m.metric_key ? T.accS : "#fff", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: isWarning(m) ? T.red : T.tx }}>{fmtValue(m)}</div>
                  {cmp?.delta != null && <span style={{ fontSize: 11, fontWeight: 600, color: deltaColor }}>{cmp.delta > 0 ? "+" : ""}{cmp.delta}{cmp.deltaPct != null ? ` (${cmp.deltaPct > 0 ? "+" : ""}${cmp.deltaPct}%)` : ""}</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.tx, marginTop: 2 }}>{m.label}</div>
                <div style={{ fontSize: 10, color: T.txM, marginTop: 2 }}>{m.description}</div>
                {m.alert_threshold != null && <div style={{ fontSize: 9, color: T.orn, marginTop: 4 }}>Alert: {m.alert_direction === "below" ? "\u2264" : "\u2265"} {m.alert_threshold}</div>}
                {m.drill_report_id && onDrillThrough && <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDrillThrough(m.drill_report_id); }} style={{ marginTop: 6, padding: "3px 8px", fontSize: 10, fontWeight: 600, backgroundColor: T.accS, color: T.acc, border: `1px solid ${T.acc}`, borderRadius: 4, cursor: "pointer", fontFamily: "inherit" }}>View Details</button>}
              </div>
              );
            })}
          </div>
        </div>
      ))}

      {selectedMetric && (
        <div style={{ marginTop: 20, backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 8, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>30-Day Trend: {metrics.find((m: any) => m.metric_key === selectedMetric)?.label}</div>
          {trendLoading && <div style={{ color: T.txM, fontSize: 13 }}>Loading trend...</div>}
          {!trendLoading && trend?.trend?.length > 0 && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 120 }}>
              {trend.trend.map((t: any, i: number) => {
                const h = Math.max(((Number(t.value) || 0) / maxTrend) * 100, 2);
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ fontSize: 8, color: T.txM, marginBottom: 2 }}>{Number(t.value)}</div>
                    <div style={{ width: "100%", height: h, backgroundColor: T.acc, borderRadius: "3px 3px 0 0", minWidth: 4 }} title={`${t.snapshot_date}: ${t.value}`} />
                    {(i === 0 || i === trend.trend.length - 1 || i % 7 === 0) && <div style={{ fontSize: 7, color: T.txM, marginTop: 2, whiteSpace: "nowrap" }}>{String(t.snapshot_date).slice(5)}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {!trendLoading && (!trend?.trend || trend.trend.length === 0) && <div style={{ color: T.txM, fontSize: 13, padding: 20, textAlign: "center" }}>No snapshot data yet. Capture a snapshot to start tracking trends.</div>}

          {/* Alert Settings */}
          {(hasPermission(user, 'admin.configure_metrics_alerts') || ["admin", "legalops"].includes(user?.role || '')) && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.brd}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Alert Settings</div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div>
                  <label style={{ fontSize: 11, color: T.txM, display: "block", marginBottom: 3 }}>Threshold</label>
                  <input type="number" value={alertThreshold} onChange={e => setAlertThreshold(e.target.value)} placeholder="e.g. 5" style={{ padding: "6px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 12, fontFamily: "inherit", width: 80 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: T.txM, display: "block", marginBottom: 3 }}>Direction</label>
                  <select value={alertDirection} onChange={e => setAlertDirection(e.target.value)} style={{ padding: "6px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 12, fontFamily: "inherit" }}>
                    <option value="above">Alert when above</option>
                    <option value="below">Alert when below</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 11, color: T.txM, display: "block", marginBottom: 3 }}>Recipients (comma-separated)</label>
                  <input value={alertRecipients} onChange={e => setAlertRecipients(e.target.value)} placeholder="user@company.com" style={{ padding: "6px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 12, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
                </div>
                <button onClick={() => { saveAlert(); }} style={{ padding: "6px 14px", backgroundColor: T.acc, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Save Alert</button>
                {alertMsg && <span style={{ fontSize: 11, color: alertMsg === "Failed" ? T.red : T.acc }}>{alertMsg}</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

